import { css } from "@compiled/react";
import { useInView } from "react-intersection-observer";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";

import { media } from "@atlaskit/primitives";
import UFOSegment from "@atlaskit/react-ufo/segment";

import { Link } from "@/libraries/design/components";
import { useLocalizedHref } from "@/libraries/web-framework";

type IconDataProps = {
    url: string;
    altText: string;
    href?: string;
};

export const Integrations = () => {
    const intl = useIntl();
    const iconData: IconDataProps[] = [
        {
            url: "https://dam-cdn.atl.orangelogic.com/AssetLink/ugn05g0l5e8847w3i0723j83hir4i7i1.webp",
            altText: intl.formatMessage({
                id: "HomePageBpsRedesignTemplate.integrations.confluence-bps-integrations-google-drive",
                defaultMessage: "Google drive logo"
            }),
            href: useLocalizedHref(
                "https://marketplace.atlassian.com/apps/1213092/google-drive-for-confluence-official-legacy-editor-only"
            )
        },
        {
            url: "https://dam-cdn.atl.orangelogic.com/AssetLink/78u3ukk3gbf60864vl53i0077y7vya51.webp",
            altText: intl.formatMessage({
                id: "HomePageBpsRedesignTemplate.integrations.confluence-bps-integrations-mailchimp",
                defaultMessage: "Mail chimp logo"
            }),
            href: "https://marketplace.atlassian.com/apps/1230718/mailchimp-for-confluence"
        },
        {
            url: "https://dam-cdn.atl.orangelogic.com/AssetLink/4y7w7a1w84hhud40mwc5ci084o7a458b.webp",
            altText: intl.formatMessage({
                id: "HomePageBpsRedesignTemplate.integrations.confluence-bps-integrations-figma",
                defaultMessage: "Figma logo"
            }),
            href: "https://marketplace.atlassian.com/apps/1221692/figma-for-confluence"
        },
        {
            url: "https://dam-cdn.atl.orangelogic.com/AssetLink/k030m35oc35qp18fk0c0c2124773u0d3.webp",
            altText: intl.formatMessage({
                id: "HomePageBpsRedesignTemplate.integrations.confluence-bps-integrations-miro",
                defaultMessage: "Miro logo"
            }),
            href: "https://marketplace.atlassian.com/apps/1217530/miro-for-confluence"
        },
        {
            url: "https://dam-cdn.atl.orangelogic.com/AssetLink/8ll520y26k47ho1feb083j4b554i2fag.webp",
            altText: intl.formatMessage({
                id: "HomePageBpsRedesignTemplate.integrations.confluence-bps-integrations-slack",
                defaultMessage: "Slack logo"
            }),
            href: "https://marketplace.atlassian.com/search?query=slack&product=confluence"
        },
        {
            url: "https://dam-cdn.atl.orangelogic.com/AssetLink/gx070nual3j71rw23ygur85n25h2e2xi.webp",
            altText: intl.formatMessage({
                id: "HomePageBpsRedesignTemplate.integrations.confluence-bps-integrations-zoom",
                defaultMessage: "Zoom logo"
            }),
            href: "https://marketplace.atlassian.com/search?query=zoom&product=confluence"
        }
    ];

    const iconDataSecond: IconDataProps[] = [
        {
            url: "https://dam-cdn.atl.orangelogic.com/AssetLink/n1t54q21qra6b15358258a30k8g202c3.webp",
            altText: intl.formatMessage({
                id: "HomePageBpsRedesignTemplate.integrations.confluence-bps-integrations-teams",
                defaultMessage: "Microsoft teams logo"
            }),
            href: "https://marketplace.atlassian.com/apps/1226127/confluence-cloud-for-microsoft-teams-official"
        },
        {
            url: "https://dam-cdn.atl.orangelogic.com/AssetLink/k41m8m520gar01eg67f475w0yd851j60.webp",
            altText: intl.formatMessage({
                id: "HomePageBpsRedesignTemplate.integrations.confluence-bps-integrations-salesforce",
                defaultMessage: "Salesforce logo"
            }),
            href: "https://marketplace.atlassian.com/search?query=salesforce&product=confluence"
        },
        {
            url: "https://dam-cdn.atl.orangelogic.com/AssetLink/xm2tp3640ol77ki47f155l6m147k7yxs.webp",
            altText: intl.formatMessage({
                id: "HomePageBpsRedesignTemplate.integrations.confluence-bps-integrations-google-docs",
                defaultMessage: "Google docs logo"
            }),
            href: "https://marketplace.atlassian.com/search?query=google+docs&product=confluence"
        },
        {
            url: "https://dam-cdn.atl.orangelogic.com/AssetLink/flp4q2lyyn5w3pil3a2l2j06p2008g61.webp",
            altText: intl.formatMessage({
                id: "HomePageBpsRedesignTemplate.integrations.confluence-bps-integrations-microsoft",
                defaultMessage: "Microsoft Suite logo"
            }),
            href: "https://marketplace.atlassian.com/search?query=microsoft&product=confluence"
        },
        {
            url: "https://dam-cdn.atl.orangelogic.com/AssetLink/nb287lrdd3508553k11w3471hrloi814.webp",
            altText: intl.formatMessage({
                id: "HomePageBpsRedesignTemplate.integrations.confluence-bps-integrations-github",
                defaultMessage: "Github logo"
            }),
            href: "https://marketplace.atlassian.com/search?query=github+&product=confluence"
        },
        {
            url: "https://dam-cdn.atl.orangelogic.com/AssetLink/7r7v2j0f02g71nr8dh3177138cio5wx1.webp",
            altText: intl.formatMessage({
                id: "HomePageBpsRedesignTemplate.integrations.slack-logo-alt",
                defaultMessage: "PowerBI logo"
            }),
            href: "https://marketplace.atlassian.com/search?query=powerbi"
        }
    ];

    const iconDataMobile = [...iconData, ...iconDataSecond];
    iconDataMobile.splice(9, 1);
    iconDataMobile.splice(10, 1);

    return (
        <UFOSegment name="paid-lp-xflow-redesign-experiment-integrations-hero">
            <section id="integrationsHero" css={integrationHeroStyles}>
                <IconCollectionBPS
                    iconDataLeft={iconData}
                    iconDataMobile={iconDataMobile}
                    iconDataRight={iconDataSecond}
                />
            </section>
        </UFOSegment>
    );
};

const integrationHeroStyles = css({
    "backgroundColor": "#F8F8F8",
    "marginBlockStart": "40px",
    "paddingBlock": "32px",

    [media.above.md]: {
        marginBlockStart: "55px",
        paddingBlock: "55px"
    },
    "@media only screen and (max-width: 767px)": {
        clipPath: "polygon(0% 4.458%,100% 0%,100% 100%,0% 100%,0% 4.458%)"
    }
});

type IconCollectionProps = {
    iconDataLeft: IconDataProps[];
    iconDataMobile: IconDataProps[];
    iconDataRight: IconDataProps[];
};
export const IconCollectionBPS = ({
    iconDataLeft,
    iconDataMobile,
    iconDataRight
}: IconCollectionProps) => {
    const { ref: scrollRef, inView: sectionIsVisible } = useInView();

    return (
        <div css={sectionContainer}>
            <div css={[iconContainerStyles, end]} ref={scrollRef}>
                {iconDataLeft.map((icon, index) =>
                    icon.href ? (
                        <a
                            key={index}
                            href={icon.href}
                            css={[
                                iconStyles,
                                linkStyles,
                                sectionIsVisible ? animationStyle : null,
                                index == 5 ? iconLeftMarginStyles : null,
                                index === 0 ? iconRightMarginStyles : null
                            ]}
                            style={{
                                animationDelay: `${0.5 + 0.25 * index}s`
                            }}
                            data-event-container="integrationsSection"
                            data-event-component="linkButton"
                            data-label={icon.altText}
                            data-event="clicked"
                            data-track-event="integrations.icon"
                        >
                            {/* eslint-disable-next-line no-restricted-syntax */}
                            <img src={icon.url} alt={icon.altText} />
                        </a>
                    ) : (
                        <div
                            key={index}
                            css={[
                                iconStyles,
                                sectionIsVisible ? animationStyle : null
                            ]}
                            style={{
                                animationDelay: `${0.5 + 0.25 * index}s`
                            }}
                        >
                            {/* eslint-disable-next-line no-restricted-syntax */}
                            <img src={icon.url} alt={icon.altText} />
                        </div>
                    )
                )}
            </div>

            <div css={centerTextStyles}>
                <h2 css={headerOverrides}>
                    <FormattedMessage
                        id="HomePageTemplateV2.confluenceHero"
                        defaultMessage="Integrate all your {br} favorite tools"
                        values={{
                            br: <br />
                        }}
                    />
                </h2>
                <Link
                    css={centerTextLinkStyles}
                    text={"Explore all 3,000+ app integrations"}
                    type="primary"
                    href={
                        "https://marketplace.atlassian.com/addons/app/confluence"
                    }
                    dataEventProps={{
                        "data-event": "clicked",
                        "data-event-component": "link",
                        "data-event-container": "additionalResourcesSection",
                        "data-product-key":
                            "confluence-PaidLpXflowRedesignExperiment",
                        "data-label-english":
                            "Explore all 3,000+ app integrations"
                    }}
                />
            </div>
            <div css={[iconContainerStyles, start]} ref={scrollRef}>
                {iconDataRight.map((icon, index) =>
                    icon.href ? (
                        <a
                            key={index}
                            href={icon.href}
                            css={[
                                iconStyles,
                                linkStyles,
                                sectionIsVisible ? animationStyle : null,
                                index == 3 ? iconRightMarginStyles : null
                            ]}
                            style={{
                                animationDelay: `${0.5 + 0.25 * index}s`
                            }}
                            data-event-container="integrationsSection"
                            data-event-component="linkButton"
                            data-label={icon.altText}
                            data-event="clicked"
                            data-track-event="integrations.icon"
                        >
                            {/* eslint-disable-next-line no-restricted-syntax */}
                            <img src={icon.url} alt={icon.altText} />
                        </a>
                    ) : (
                        <div
                            key={index}
                            css={[
                                iconStyles,
                                sectionIsVisible ? animationStyle : null
                            ]}
                            style={{
                                animationDelay: `${0.5 + 0.25 * index}s`
                            }}
                        >
                            {/* eslint-disable-next-line no-restricted-syntax */}
                            <img src={icon.url} alt={icon.altText} />
                        </div>
                    )
                )}
            </div>
            <div css={[iconContainerStyles, center]} ref={scrollRef}>
                {iconDataMobile.map((icon, index) =>
                    icon.href ? (
                        <a
                            key={index}
                            href={icon.href}
                            css={[
                                iconStyles,
                                linkStyles,
                                sectionIsVisible ? animationStyle : null,
                                index == 0 || index == 7
                                    ? iconLeftMobileMarginStyles
                                    : null,
                                index == 2 || index == 9
                                    ? iconRightMobileMarginStyles
                                    : null
                            ]}
                            style={{
                                animationDelay: `${0.5 + 0.25 * index}s`
                            }}
                            data-event-container="integrationsSection"
                            data-event-component="linkButton"
                            data-label={icon.altText}
                            data-event="clicked"
                            data-track-event="integrations.icon"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {/* eslint-disable-next-line no-restricted-syntax */}
                            <img src={icon.url} alt={icon.altText} />
                        </a>
                    ) : (
                        <div
                            key={index}
                            css={[
                                iconStyles,
                                sectionIsVisible ? animationStyle : null
                            ]}
                            style={{
                                animationDelay: `${0.5 + 0.25 * index}s`
                            }}
                        >
                            {/* eslint-disable-next-line no-restricted-syntax */}
                            <img src={icon.url} alt={icon.altText} />
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

const sectionContainer = css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    marginBlock: "2rem",
    gap: 0,
    [media.only.md]: {
        gap: 12
    },
    [media.only.sm]: {
        gap: 32
    },
    [media.below.md]: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        gap: 32
    }
});

const iconContainerStyles = css({
    display: "flex",
    flexWrap: "wrap",
    flexBasis: "100%",
    gap: "24px 32px",
    marginInline: "24px",
    justifyContent: "center",
    alignContent: "center",
    maxWidth: 450,
    maxHeight: 190,
    [media.only.md]: {
        gap: "20px 23px",
        maxWidth: 360,
        maxHeight: 166
    },

    [media.below.md]: {
        gap: 16
    },
    [media.below.sm]: {
        maxWidth: "75%"
    },
    [media.below.xs]: {
        maxWidth: "100%"
    }
});

const iconStyles = css({
    borderRadius: "27px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    animationIterationCount: 1,
    animationDuration: "0.75s",
    background: "#fff",
    width: "83px",
    height: "83px",
    // Related to Tech debt: Adding this disable to unblock commits for GWP-2646
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        maxWidth: "50%",
        maxHeight: "50%",
        width: "auto",
        height: "auto"
    },

    [media.below.lg]: {
        borderRadius: "23px",
        width: "73px",
        height: "73px"
    },

    [media.below.md]: {
        borderRadius: "15px",
        width: "63px",
        height: "63px"
    }
});

const start = css({
    alignContent: "flex-start",
    justifyContent: "flex-start",
    [media.below.lg]: {
        marginInlineEnd: 0,
        marginInlineStart: 12
    },
    [media.below.md]: {
        display: "none"
    }
});

const center = css({
    alignContent: "center",
    justifyContent: "center",
    marginBlockStart: 50,
    width: "100%",
    [media.above.md]: {
        display: "none"
    }
});

const end = css({
    alignContent: "flex-end",
    justifyContent: "flex-end",
    [media.below.lg]: {
        marginInlineStart: 0,
        marginInlineEnd: 12
    },
    [media.below.md]: {
        display: "none"
    }
});

const iconRightMarginStyles = css({
    marginInlineStart: "64px"
});

const iconLeftMarginStyles = css({
    marginInlineEnd: "64px"
});

const iconLeftMobileMarginStyles = css({
    marginInlineStart: "calc((100% - 63px - 63px - 63px - 32px)/2)",
    [media.above.xs]: {
        marginInlineStart: "calc((100% - 74px - 74px - 74px - 32px)/2)"
    }
});
const iconRightMobileMarginStyles = css({
    marginInlineEnd: "calc((100% - 63px - 63px - 63px - 32px)/2)",
    [media.above.xs]: {
        marginInlineEnd: "calc((100% - 74px - 74px - 74px - 32px)/2)"
    }
});

const linkStyles = css({
    "cursor": "pointer",
    "boxShadow": "0px 1px 1px 0px #091E4240,  0px 0px 1px 0px #091E424F",

    "&:hover": {
        boxShadow: "0px 8px 12px 0px #091E4226, 0px 0px 1px 0px #091E424F"
    }
});

const animationStyle = css({
    "@keyframes hopup": {
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "0%": {
            transform: "translateY(0)"
        },
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "50%": {
            transform: "translateY(-15%)"
        },
        // Tech debt: Adding this disable to unblock commits for GWP-2646
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "100%": {
            transform: "translateY(0)"
        }
    },
    "animationName": "hopup"
});

const centerTextStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 32,
    textAlign: "center",
    minWidth: "450px",
    [media.below.lg]: {
        minWidth: "300px"
    },
    [media.below.md]: {
        minWidth: "100%"
    }
});

const headerOverrides = css({
    marginBlock: 0,
    fontSize: "40px !important"
});

const centerTextLinkStyles = css({
    fontWeight: "600",
    fontsize: "16px"
});
